import { type ReactNode, useState } from "react";
import { Link, NavLink, useLocation, useNavigate } from "react-router-dom";
import { BRAND } from "@brand";

const NAV = [
  { to: "/", label: "Home" },
  { to: "/blocks", label: "Blocks" },
  { to: "/operations", label: "Operations" },
  { to: "/denunciations", label: "Denunciations" },
  { to: "/stakers", label: "Stakers" },
  { to: "/charts", label: "Charts" },
  { to: "/api", label: "API" },
];

function navCls({ isActive }: { isActive: boolean }) {
  return `px-3 py-1.5 rounded-full no-underline transition-colors ${
    isActive ? "bg-panel2 text-fg" : "text-muted hover:text-fg"
  }`;
}

/** Search box shared by the header (compact) and the Home hero (large). */
export function SearchForm({ large = false }: { large?: boolean }) {
  const [query, setQuery] = useState("");
  const navigate = useNavigate();

  function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    const q = query.trim();
    if (!q) return;
    navigate(`/search?q=${encodeURIComponent(q)}`);
  }

  return (
    <form
      onSubmit={onSubmit}
      role="search"
      className={`flex items-center gap-2 bg-panel border border-border focus-within:border-accent/60 transition-colors ${
        large
          ? "h-14 sm:h-16 pl-4 sm:pl-5 pr-2 rounded-2xl shadow-[0_20px_60px_-20px_rgb(0_0_0)] shadow-accent/30"
          : "h-10 pl-3 pr-1 rounded-full"
      }`}
    >
      <svg
        width={large ? 20 : 16}
        height={large ? 20 : 16}
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        className="text-muted shrink-0"
        aria-hidden="true"
      >
        <circle cx="11" cy="11" r="7" />
        <path d="m20 20-3.5-3.5" />
      </svg>
      <input
        type="text"
        aria-label="Search"
        placeholder={
          large
            ? "Search by block, operation, address or slot (period,thread)"
            : "Search block, op, address…"
        }
        value={query}
        onChange={(e) => setQuery(e.target.value)}
        className={`flex-1 min-w-0 h-full bg-transparent outline-none placeholder:text-muted/70 ${
          large ? "text-base" : "text-sm"
        }`}
      />
      <button
        type="submit"
        className={`shrink-0 bg-accent text-on-accent font-semibold hover:brightness-110 transition ${
          large ? "h-10 sm:h-12 px-4 sm:px-6 rounded-xl" : "h-8 px-3 rounded-full text-sm"
        }`}
      >
        Search
      </button>
    </form>
  );
}

export function Layout({ children }: { children: ReactNode }) {
  const { pathname } = useLocation();
  const isHome = pathname === "/";

  return (
    // `page-home` lets the brand's theme.css decorate the Home page background.
    <div className={`min-h-screen flex flex-col ${isHome ? "page-home" : ""}`}>
      <header className="border-b border-border bg-bg/80 backdrop-blur sticky top-0 z-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 h-16 flex items-center gap-4 lg:gap-8">
          <Link to="/" className="flex items-center gap-2.5 text-fg no-underline whitespace-nowrap">
            <img src={BRAND.emblem} alt="" width="28" height="28" />
            <span className="font-display text-lg font-semibold tracking-tight text-brand">
              {BRAND.name} <span className="text-muted font-medium">{BRAND.emblemSuffix}</span>
            </span>
          </Link>
          <nav className="hidden lg:flex gap-1 text-sm font-medium">
            {NAV.map((n) => (
              <NavLink key={n.to} to={n.to} end={n.to === "/"} className={navCls}>
                {n.label}
              </NavLink>
            ))}
          </nav>
          <div className="flex-1 min-w-0">
            {!isHome && (
              <div className="hidden md:block max-w-md ml-auto">
                <SearchForm />
              </div>
            )}
          </div>
          <span className="flex items-center gap-2 h-10 px-3 rounded-full border border-border bg-panel text-sm font-medium">
            <span className="w-2 h-2 rounded-full bg-ok ring-4 ring-ok/15" />
            Mainnet
          </span>
          <Link
            to="/settings"
            aria-label="Settings"
            className="hidden sm:flex w-10 h-10 items-center justify-center rounded-full border border-border text-muted hover:text-fg"
          >
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <path d="M4 21v-7M4 10V3M12 21v-9M12 8V3M20 21v-5M20 12V3M1 14h6M9 8h6M17 16h6" />
            </svg>
          </Link>
          {/* Home only: on other pages the header search needs the room. */}
          {isHome && BRAND.headerCta && (
            <a
              href={BRAND.headerCta.href}
              target="_blank"
              rel="noreferrer"
              className="hidden xl:inline-flex items-center h-10 px-4 rounded-full bg-accent text-on-accent hover:text-on-accent text-sm font-semibold no-underline hover:brightness-110 transition whitespace-nowrap"
            >
              {BRAND.headerCta.label}
            </a>
          )}
          {/* ponytail: native <details> as the mobile menu, no state or portal */}
          <details className="lg:hidden relative group">
            <summary
              aria-label="Menu"
              className="list-none w-10 h-10 flex items-center justify-center rounded-full border border-border cursor-pointer [&::-webkit-details-marker]:hidden"
            >
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" aria-hidden="true">
                <path d="M4 7h16M4 12h16M4 17h16" />
              </svg>
            </summary>
            <nav
              className="absolute right-0 mt-2 w-56 p-2 rounded-2xl border border-border bg-panel shadow-xl flex flex-col text-sm font-medium"
              onClick={(e) => (e.currentTarget.parentElement as HTMLDetailsElement).removeAttribute("open")}
            >
              {[...NAV, { to: "/settings", label: "Settings" }].map((n) => (
                <NavLink key={n.to} to={n.to} end={n.to === "/"} className={navCls}>
                  {n.label}
                </NavLink>
              ))}
            </nav>
          </details>
        </div>
        {!isHome && (
          <div className="md:hidden px-4 pb-3">
            <SearchForm />
          </div>
        )}
      </header>
      <main className="flex-1">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-6 sm:py-8">
          {children}
        </div>
      </main>
      <Footer />
    </div>
  );
}

function Footer() {
  const f = BRAND.footer;
  return (
    <footer className="border-t border-border">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-10 sm:py-12 flex flex-col gap-10">
        <div className="flex flex-wrap items-center justify-between gap-x-8 gap-y-4 text-sm">
          <div className="flex flex-col items-start gap-1">
            {f.logo ? (
              <a href={f.logo.href} target="_blank" rel="noreferrer">
                <img src={f.logo.src} alt={f.logo.alt} className="h-6 w-auto" />
              </a>
            ) : (
              <span className="text-lg font-semibold tracking-tight">{f.wordmark}</span>
            )}
            {f.tagline && <p className="text-xs font-medium text-accent">{f.tagline}</p>}
          </div>
          {f.email && (
            <a href={`mailto:${f.email}`} className="text-muted hover:text-fg no-underline">
              {f.email}
            </a>
          )}
          {f.links && (
            <nav aria-label={`${BRAND.name} links`} className="flex flex-col gap-1">
              {f.links.map(([label, href]) => (
                <a key={href} href={href} target="_blank" rel="noreferrer" className="text-muted hover:text-fg no-underline">
                  {label}
                </a>
              ))}
            </nav>
          )}
          {f.copyright && (
            <p className="text-muted">
              © {new Date().getFullYear()}{" "}
              <a href={f.copyright[1]} target="_blank" rel="noreferrer" className="text-muted hover:text-fg no-underline">
                {f.copyright[0]}
              </a>
            </p>
          )}
          {f.social && (
            <nav aria-label={`${BRAND.name} social`} className="flex flex-wrap items-center gap-3">
              {f.social.map(([label, href, icon]) => (
                <a
                  key={href}
                  href={href}
                  target="_blank"
                  rel="noreferrer"
                  aria-label={label}
                  title={label}
                  className="text-muted hover:text-fg transition-colors"
                >
                  {/* ponytail: CSS mask so the single-colour SVGs take currentColor */}
                  <span
                    aria-hidden="true"
                    className="block w-[26px] h-[26px] bg-current"
                    style={{
                      mask: `url("${icon}") center / contain no-repeat`,
                      WebkitMask: `url("${icon}") center / contain no-repeat`,
                    }}
                  />
                </a>
              ))}
            </nav>
          )}
        </div>
        {f.columns && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {f.columns.map(([title, links]) => (
              <nav key={title} aria-label={title} className="flex flex-col gap-2 text-sm">
                <h2 className="text-fg font-semibold mb-1">{title}</h2>
                {links.map(([label, href]) => (
                  <a key={href + label} href={href} target="_blank" rel="noreferrer" className="text-muted hover:text-fg no-underline">
                    {label}
                  </a>
                ))}
              </nav>
            ))}
          </div>
        )}
      </div>
    </footer>
  );
}
